/**
 * Storage helper utilities for localStorage operations
 */

/**
 * Get an item from localStorage
 * @param key - The key to retrieve
 * @returns The stored value or null if not found
 */
export function getLocalStorageItem(key: string): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return localStorage.getItem(key)
  } catch (error) {
    console.error('Error getting localStorage item:', error)
    return null
  }
}

/**
 * Set an item in localStorage
 * @param key - The key to store
 * @param value - The value to store
 * @returns True if successful, false otherwise
 */
export function setLocalStorageItem(key: string, value: string, expiresIn: number = 0): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    localStorage.setItem(key, value)
    if (expiresIn > 0) {
      setTimeout(
        () => {
          localStorage.removeItem(key)
        },
        expiresIn * 60 * 1000
      )
    }
    return true
  } catch (error) {
    console.error('Error setting localStorage item:', error)
    return false
  }
}

/**
 * Remove an item from localStorage
 * @param key - The key to remove
 * @returns True if successful, false otherwise
 */
export function removeLocalStorageItem(key: string): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    localStorage.removeItem(key)
    return true
  } catch (error) {
    console.error('Error removing localStorage item:', error)
    return false
  }
}

/**
 * Clear all items from localStorage
 * @returns True if successful, false otherwise
 */
export function clearLocalStorage(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    localStorage.clear()
    return true
  } catch (error) {
    console.error('Error clearing localStorage:', error)
    return false
  }
}

/**
 * Convert base64 string to UTF-8 string
 * @param base64 - The base64 encoded string
 * @returns The decoded UTF-8 string
 */
export function base64ToUtf8(base64: string): string {
  try {
    if (typeof window === 'undefined') {
      return ''
    }
    return atob(base64)
  } catch (error) {
    console.error('Error decoding base64:', error)
    return ''
  }
}

/**
 * Convert UTF-8 string to base64 string
 * @param str - The UTF-8 string to encode
 * @returns The base64 encoded string
 */
export function utf8ToBase64(str: string): string {
  try {
    if (typeof window === 'undefined') {
      return ''
    }
    return btoa(str)
  } catch (error) {
    console.error('Error encoding to base64:', error)
    return ''
  }
}

/**
 * Get and parse JSON from localStorage
 * @param key - The key to retrieve
 * @returns The parsed object or null if not found/invalid
 */
export function getLocalStorageJSON<T = any>(key: string): T | null {
  const item = getLocalStorageItem(key)
  if (!item) {
    return null
  }

  try {
    return JSON.parse(item)
  } catch (error) {
    console.error('Error parsing localStorage JSON:', error)
    return null
  }
}

/**
 * Set JSON object in localStorage
 * @param key - The key to store
 * @param value - The object to store
 * @returns True if successful, false otherwise
 */
export function setLocalStorageJSON(key: string, value: any): boolean {
  try {
    const jsonString = JSON.stringify(value)
    return setLocalStorageItem(key, jsonString)
  } catch (error) {
    console.error('Error stringifying JSON for localStorage:', error)
    return false
  }
}
